import { lazy, Suspense } from 'react';
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieConsent } from "@/components/CookieConsent";
import Index from "./pages/Index";

/* Lazy-loaded pages – reduces initial bundle by ~169 KiB */
const Leistungen = lazy(() => import('./pages/Leistungen'));
const Maschinenpark = lazy(() => import('./pages/Maschinenpark'));
const Karriere = lazy(() => import('./pages/Karriere'));
const Nachhaltigkeit = lazy(() => import('./pages/Nachhaltigkeit'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const AGB = lazy(() => import('./pages/AGB'));
const Consulting = lazy(() => import('./pages/Consulting'));
const UeberUns = lazy(() => import('./pages/UeberUns'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Admin = lazy(() => import('./pages/Admin'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

/* SEO landing pages — not linked from navigation, indexed via sitemap */
const KIBeratungMittelstand = lazy(() => import('./pages/landing/KIBeratungMittelstand'));
const KIReadinessCheck = lazy(() => import('./pages/landing/KIReadinessCheck'));
const PredictiveMaintenance = lazy(() => import('./pages/landing/PredictiveMaintenance'));
const EUAIActBeratung = lazy(() => import('./pages/landing/EUAIActBeratung'));
const Prozessautomatisierung = lazy(() => import('./pages/landing/Prozessautomatisierung'));
const KIFoerderungKMU = lazy(() => import('./pages/landing/KIFoerderungKMU'));
const CncMachiningFrankfurt = lazy(() => import('./pages/landing/CncMachiningFrankfurt'));
const PrecisionMachiningRhineMain = lazy(() => import('./pages/landing/PrecisionMachiningRhineMain'));
const GeoLanding = lazy(() => import('./pages/landing/GeoLanding'));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              {/* Localized routes: rendered for both `/<path>` and `/en/<path>`.
                  Active language is derived from the URL via useLang(). */}
              {(['', '/en'] as const).map((prefix) => (
                <Route key={prefix || 'root'}>
                  <Route path={`${prefix}/`} element={<Index />} />
                  <Route path={`${prefix}/leistungen`} element={<Leistungen />} />
                  <Route path={`${prefix}/consulting`} element={<Consulting />} />
                  <Route path={`${prefix}/maschinenpark`} element={<Maschinenpark />} />
                  <Route path={`${prefix}/karriere`} element={<Karriere />} />
                  <Route path={`${prefix}/nachhaltigkeit`} element={<Nachhaltigkeit />} />
                  <Route path={`${prefix}/ueber-uns`} element={<UeberUns />} />
                  <Route path={`${prefix}/kontakt`} element={<Kontakt />} />
                  <Route path={`${prefix}/impressum`} element={<Impressum />} />
                  <Route path={`${prefix}/datenschutz`} element={<Datenschutz />} />
                  <Route path={`${prefix}/agb`} element={<AGB />} />
                  <Route path={`${prefix}/blog`} element={<Blog />} />
                  <Route path={`${prefix}/blog/:slug`} element={<BlogPost />} />
                  <Route path={`${prefix}/ki-beratung-mittelstand`} element={<KIBeratungMittelstand />} />
                  <Route path={`${prefix}/ki-readiness-check`} element={<KIReadinessCheck />} />
                  <Route path={`${prefix}/predictive-maintenance-kmu`} element={<PredictiveMaintenance />} />
                  <Route path={`${prefix}/eu-ai-act-beratung`} element={<EUAIActBeratung />} />
                  <Route path={`${prefix}/prozessautomatisierung-mittelstand`} element={<Prozessautomatisierung />} />
                  <Route path={`${prefix}/ki-foerderung-kmu`} element={<KIFoerderungKMU />} />
                  <Route path={`${prefix}/cnc-machining-frankfurt`} element={<CncMachiningFrankfurt />} />
                  <Route path={`${prefix}/precision-machining-rhine-main`} element={<PrecisionMachiningRhineMain />} />
                </Route>
              ))}
              {/* DE-only geo & category landings (nationwide + international reach) */}
              <Route path="/cnc-fraesen-deutschland" element={<GeoLanding />} />
              <Route path="/cnc-drehen-deutschland" element={<GeoLanding />} />
              <Route path="/cnc-lohnfertigung" element={<GeoLanding />} />
              <Route path="/praezisionsteile-hersteller" element={<GeoLanding />} />
              <Route path="/zerspanung-auftragsfertigung" element={<GeoLanding />} />
              <Route path="/cnc-fertigung-rhein-main" element={<GeoLanding />} />
              <Route path="/cnc-fertigung-stuttgart" element={<GeoLanding />} />
              <Route path="/cnc-fertigung-muenchen" element={<GeoLanding />} />
              <Route path="/cnc-fertigung-hamburg" element={<GeoLanding />} />
              {/* EN-only category landings */}
              <Route path="/en/cnc-milling-germany" element={<GeoLanding />} />
              <Route path="/en/cnc-turning-germany" element={<GeoLanding />} />
              <Route path="/en/cnc-contract-manufacturing" element={<GeoLanding />} />
              <Route path="/en/precision-parts-manufacturer" element={<GeoLanding />} />
              <Route path="/en/precision-machining-europe" element={<GeoLanding />} />
              {/* Admin stays language-agnostic */}
              <Route path="/admin/anfragen" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
